'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  DatePicker,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import type {
  CommendaOSFieldConfig,
  DataSourceConfig,
  FormDSL,
} from '@commenda/types';
import { useEffect, useMemo, useState } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import {
  type DocumentEffectivePeriod,
  DocumentEffectivePeriodHelper,
} from '../../utils/document-effective-period-helper';
import { ContextBindingEditor } from './ContextBindingEditor';
import {
  getContextSourceFields,
  getEntityContextKeys,
} from './context-helpers';

export function DocumentEditor({
  props,
  dataSource,
  onUpdateProps,
  onDataSourceChange,
  currentFieldId,
  dsl,
}: {
  props: Record<string, unknown>;
  dataSource?: DataSourceConfig;
  onUpdateProps: (key: string, value: unknown) => void;
  onDataSourceChange: (dataSource: DataSourceConfig | undefined) => void;
  currentFieldId?: string;
  dsl?: FormDSL;
}) {
  const { entitySchema, isLoading } = useCommendaOSEntitySchema('document');

  const documentSubTypeOptions = useMemo(() => {
    if (!entitySchema?.filters?.documentSubType?.enumOptions) return [];
    return entitySchema.filters.documentSubType.enumOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
      isTimeSensitive: opt.isTimeSensitive ?? false,
    }));
  }, [entitySchema]);

  const selectedDocumentSubType = (props?.documentSubType as string) || '';
  const mode = (props?.mode as 'read' | 'read-write') || 'read-write';
  const commendaOS = dataSource?.commendaOS;

  const savedPeriod: DocumentEffectivePeriod = {
    effectiveStartDate:
      typeof props?.effectiveStartDate === 'string'
        ? props.effectiveStartDate
        : undefined,
    effectiveEndDate:
      typeof props?.effectiveEndDate === 'string'
        ? props.effectiveEndDate
        : undefined,
  };

  // An invalid selection is shown with an error but never saved to the template.
  const [rejectedPeriod, setRejectedPeriod] = useState<{
    fieldId: string | undefined;
    period: DocumentEffectivePeriod;
  } | null>(null);
  const displayedPeriod =
    rejectedPeriod && rejectedPeriod.fieldId === currentFieldId
      ? rejectedPeriod.period
      : savedPeriod;
  const isPeriodValid = DocumentEffectivePeriodHelper.isValid(displayedPeriod);

  const handlePeriodChange = (
    key: keyof DocumentEffectivePeriod,
    date: string | undefined
  ) => {
    const nextPeriod = { ...displayedPeriod, [key]: date };
    if (!DocumentEffectivePeriodHelper.isValid(nextPeriod)) {
      setRejectedPeriod({ fieldId: currentFieldId, period: nextPeriod });
      return;
    }
    setRejectedPeriod(null);
    onUpdateProps('effectiveStartDate', nextPeriod.effectiveStartDate);
    onUpdateProps('effectiveEndDate', nextPeriod.effectiveEndDate);
  };

  const handleDocumentSubTypeChange = (value: string) => {
    onUpdateProps('documentSubType', value);
    const isTimeSensitive = documentSubTypeOptions.find(
      (opt) => opt.value === value
    )?.isTimeSensitive;
    if (isTimeSensitive) return;
    setRejectedPeriod(null);
    if (savedPeriod.effectiveStartDate || savedPeriod.effectiveEndDate) {
      onUpdateProps('effectiveStartDate', undefined);
      onUpdateProps('effectiveEndDate', undefined);
    }
  };

  const contextSourceFields = useMemo(() => {
    if (!dsl) return [];
    return getContextSourceFields(dsl, currentFieldId, 'corporation');
  }, [dsl, currentFieldId]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  useEffect(() => {
    if (!dataSource || dataSource.type !== COMMENDA_OS_DATA_SOURCE) {
      onDataSourceChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'document',
          contextBindings: [],
        },
      });
    }
  }, [dataSource, onDataSourceChange]);

  const updateCommendaOS = (updates: Partial<CommendaOSFieldConfig>) => {
    onDataSourceChange({
      type: COMMENDA_OS_DATA_SOURCE,
      commendaOS: {
        entity: 'document',
        contextBindings: commendaOS?.contextBindings,
        ...updates,
      },
    });
  };

  const selectedSubTypeMeta = documentSubTypeOptions.find(
    (opt) => opt.value === selectedDocumentSubType
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Document Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Document Sub-Type Selection */}
        <div className="space-y-2">
          <Label>Document Type</Label>
          {isLoading ? (
            <div className="flex items-center gap-2 p-2">
              <LoadingIndicator size={16} />
              <span className="txt-small-regular text-text-tertiary">
                Loading document types...
              </span>
            </div>
          ) : (
            <Select
              value={selectedDocumentSubType}
              onValueChange={handleDocumentSubTypeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select document type..." />
              </SelectTrigger>
              <SelectContent>
                {documentSubTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedSubTypeMeta && (
            <p className="txt-xs-regular text-text-tertiary">
              Selected: {selectedSubTypeMeta.label}
            </p>
          )}
        </div>

        {selectedSubTypeMeta?.isTimeSensitive && (
          <div className="space-y-md">
            <Label>Effective period (optional)</Label>
            <div className="grid grid-cols-2 gap-md">
              <div className="space-y-xs">
                <p className="txt-xs-regular text-text-secondary">Start date</p>
                <DatePicker
                  date={displayedPeriod.effectiveStartDate ?? undefined}
                  setDate={(date) =>
                    handlePeriodChange('effectiveStartDate', date)
                  }
                  placeholder="Start date"
                  hasError={!isPeriodValid}
                />
              </div>
              <div className="space-y-xs">
                <p className="txt-xs-regular text-text-secondary">End date</p>
                <DatePicker
                  date={displayedPeriod.effectiveEndDate ?? undefined}
                  setDate={(date) =>
                    handlePeriodChange('effectiveEndDate', date)
                  }
                  placeholder="End date"
                  hasError={!isPeriodValid}
                />
              </div>
            </div>
            {isPeriodValid ? (
              <p className="txt-xs-regular text-text-tertiary">
                Uploaded files are saved for this period, so uploads for other
                periods never replace them. Leave empty to save the file without
                a period.
              </p>
            ) : (
              <p className="txt-xs-regular text-text-error">
                End date must be on or after the start date.
              </p>
            )}
          </div>
        )}

        {/* Mode Selection */}
        <div className="space-y-2">
          <Label>Mode</Label>
          <Select
            value={mode}
            onValueChange={(value) => onUpdateProps('mode', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              <SelectItem value="read-write">Read &amp; Write</SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Read Only: Display document status only. Read &amp; Write: Allow
            uploading or replacing the document.
          </p>
        </div>

        {/* Context Source - corporationId binding */}
        <ContextBindingEditor
          entity="corporation"
          contextKeys={getEntityContextKeys('corporation')}
          contextBindings={commendaOS?.contextBindings}
          availableFields={contextSourceFields}
          formContextKeys={formContextKeys}
          onChange={(bindings) =>
            updateCommendaOS({ contextBindings: bindings })
          }
        />
      </div>
    </div>
  );
}
